/**
 * Shared notebook (instruction-sheet) primitives — the cream ruled paper with
 * a red margin rule and three punched holes, plus the field / task / divider
 * style fragments and the CSS the inputs need.
 *
 * Single source of truth for the notebook aesthetic, reused by:
 *   - SubmitPrepRequest.jsx (the Adviser submission form)
 *   - PrepRequestDetail.jsx (the read-only detail view)
 *
 * Tokens come straight from the approved instruction-sheet mockup.
 */

import React from 'react';

export const RULED = 'rgba(60, 110, 170, 0.20)';
export const MARGIN = '#d4565a';

// CSS that can't be expressed inline: ruled-line input styling, hover-reveal
// of the remove control, placeholder colour, and native date control trimming.
export const notebookCss = `
.np-input {
  border: none;
  background: transparent;
  outline: none;
  flex: 1;
  font-family: 'Caveat', cursive;
  color: #1f3a5c;
  line-height: 1;
  padding: 0 4px 4px;
}
.np-value { font-size: 24px; font-weight: 700; }
/* Task lines are auto-growing textareas (see SubmitPrepRequest): one ruled
   line tall to begin with, growing by exactly one 36px ruled line for every
   wrapped line, so a long task stays fully visible and each of its lines
   lands on a rule instead of scrolling sideways on one line.
   overflow:hidden keeps the measured scrollHeight clean (no inner scrollbar);
   resize:none because the height is managed for the writer. */
.np-task-input {
  font-size: 22px;
  font-weight: 500;
  line-height: 36px;
  padding: 0 4px;
  display: block;
  resize: none;
  overflow: hidden;
}
.np-input::placeholder { color: rgba(31, 58, 92, 0.3); font-style: italic; font-weight: 400; }
.np-date {
  font-family: 'Caveat', cursive;
  font-size: 22px;
  font-weight: 700;
  color: #1f3a5c;
  flex: 0 0 auto;
  max-width: 200px;
}
.np-task-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #d4565a;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;
  flex: 0 0 auto;
}
.np-task-row:hover .np-task-remove,
.np-task-remove:focus-visible { opacity: 0.7; }
/* Row tools (indent / outdent / move / remove) are hover-revealed on a
   pointer device and ALWAYS visible on touch, where there is no hover. A
   disabled tool (e.g. indent on the first line) stays faint. */
@media (hover: none) {
  .np-task-remove { opacity: 0.55; }
}
.np-task-remove:disabled { opacity: 0.15; cursor: default; }
.np-task-row:hover .np-task-remove:disabled { opacity: 0.15; }
.np-add-task {
  margin-top: 4px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'Kalam', cursive;
  font-weight: 700;
  font-size: 15px;
  color: #4a6182;
  text-align: left;
}
.np-add-task:hover { color: #F2682A; }
.np-select {
  border: none;
  background: transparent;
  outline: none;
  font-family: 'Caveat', cursive;
  font-size: 22px;
  font-weight: 700;
  color: #1f3a5c;
  cursor: pointer;
  flex: 0 0 auto;
  padding: 0 4px 4px;
}
.np-textarea {
  border: none;
  background: transparent;
  outline: none;
  width: 100%;
  font-family: 'Caveat', cursive;
  font-size: 20px;
  font-weight: 500;
  color: #1f3a5c;
  line-height: 28px;
  resize: vertical;
  min-height: 84px;
  padding: 0 4px 4px;
}
.np-textarea::placeholder { color: rgba(31, 58, 92, 0.3); font-style: italic; font-weight: 400; }
.np-choice {
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'Caveat', cursive;
  font-size: 22px;
  font-weight: 700;
  color: rgba(31, 58, 92, 0.32);
  padding: 0 2px 2px;
}
.np-choice.is-selected { color: #1f3a5c; border-bottom: 2px solid #d4565a; }
.np-choice.is-selected.urgent { color: #C0392B; border-bottom-color: #C0392B; }
.spin { animation: spin 0.8s linear infinite; }
`;

// Shared style fragments used by both the form and the detail view.
export const nb = {
  paper: {
    position: 'relative',
    background: '#fbf6e7',
    borderRadius: '2px 2px 6px 6px',
    // 56px top/right/bottom, 104px left to clear the margin rule + holes.
    padding: '56px 56px 56px 104px',
    boxShadow:
      '0 4px 14px rgba(0,0,0,0.07), 0 14px 36px rgba(0,0,0,0.10), inset 0 0 100px rgba(140,110,50,0.06)',
    // Layer 1: red margin rule (vertical, 84–85px). Layer 2: blue ruled lines @36px.
    backgroundImage: `linear-gradient(to right, transparent 84px, ${MARGIN} 84px, ${MARGIN} 85px, transparent 85px), repeating-linear-gradient(to bottom, transparent 0, transparent 35px, ${RULED} 35px, ${RULED} 36px)`,
    backgroundPosition: '0 16px, 0 50px',
    minHeight: '360px',
  },
  hole: {
    position: 'absolute',
    left: '28px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: '#F0F4F8',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.28), 0 1px 0 rgba(255,255,255,0.4)',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '14px',
    minHeight: '36px',
    marginBottom: '14px',
    flexWrap: 'wrap',
  },
  label: {
    fontFamily: "'Kalam', cursive",
    fontWeight: 700,
    fontSize: '15px',
    color: '#4a6182',
    minWidth: '132px',
    whiteSpace: 'nowrap',
    flex: '0 0 auto',
  },
  readValue: {
    fontFamily: "'Caveat', cursive",
    fontWeight: 700,
    fontSize: '24px',
    color: '#1f3a5c',
    flex: '0 0 auto',
  },
  deadline: {
    fontFamily: "'Kalam', cursive",
    fontWeight: 700,
    fontSize: '13px',
    color: '#d4565a',
    flex: '0 0 auto',
  },
  deadlineTight: {
    background: 'rgba(212, 86, 90, 0.14)',
    borderRadius: '4px',
    padding: '2px 6px',
  },
  divider: {
    fontFamily: "'Kalam', cursive",
    fontWeight: 700,
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    color: '#4a6182',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    margin: '6px 0',
  },
  tasks: {
    marginTop: 0,
  },
  taskRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    minHeight: '36px',
    position: 'relative',
  },
  taskNum: {
    fontFamily: "'Kalam', cursive",
    fontWeight: 700,
    fontSize: '17px',
    color: '#4a6182',
    minWidth: '24px',
    flex: '0 0 auto',
  },
  summaryText: {
    fontFamily: "'Caveat', cursive",
    fontWeight: 500,
    fontSize: '22px',
    color: '#1f3a5c',
  },
};

/**
 * The cream notebook page: ruled lines, red margin rule, three punched holes.
 */
export function NotebookPage({ children, minHeight }) {
  return (
    <div style={{ ...nb.paper, ...(minHeight ? { minHeight } : {}) }}>
      <span style={{ ...nb.hole, top: '80px' }} aria-hidden="true" />
      <span style={{ ...nb.hole, top: '50%', transform: 'translateY(-50%)' }} aria-hidden="true" />
      <span style={{ ...nb.hole, bottom: '80px' }} aria-hidden="true" />
      {children}
    </div>
  );
}

/**
 * A read-only "Label: value" row on the notebook.
 */
export function ReadOnlyField({ label, value }) {
  return (
    <div style={nb.fieldRow}>
      <span style={nb.label}>{label}</span>
      <span style={nb.readValue}>{value}</span>
    </div>
  );
}
